import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useState } from 'react';
import { RouterProvider } from 'react-router';

import { Spinner } from '@/components/ui';
import type { TmaBootstrap } from '@/lib/tma';
import { createQueryClient, createTrpcClient, trpc } from '@/lib/trpc';

import { NotInTelegramScreen } from './NotInTelegramScreen';
import { router } from './router';
import { TmaProvider } from './TmaProvider';

// Лендинг живёт отдельным чанком: в Mini App он не нужен (Stage 15, задача 4)
const CorpScreen = lazy(() =>
  import('@/routes/CorpScreen').then((m) => ({ default: m.CorpScreen })),
);
const PayReturnScreen = lazy(() =>
  import('@/routes/PayReturnScreen').then((m) => ({ default: m.PayReturnScreen })),
);

/** Ссылка обратно в Mini App: адрес бота из сборки, иначе — общий вид `t.me/<бот>`. */
const BOT_URL = import.meta.env.VITE_BOT_URL ?? 'https://t.me/MeetUpMenBot';

interface AppProps {
  tma: TmaBootstrap;
}

export function App({ tma }: AppProps) {
  const [queryClient] = useState(createQueryClient);
  const [trpcClient] = useState(createTrpcClient);

  // Лендинг корпоративных сезонов (Stage 14, задача 4) открывают с сайта, а не из Telegram:
  // `corp.*` — публичные процедуры, initData им не нужен, поэтому TmaProvider не оборачиваем.
  if (window.location.pathname.startsWith('/corp')) {
    return (
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<Spinner />}>
            <CorpScreen />
          </Suspense>
        </QueryClientProvider>
      </trpc.Provider>
    );
  }

  // Возврат из платёжной системы приходит во внешний браузер, а не в Telegram: показываем итог
  // и уводим обратно в приложение. Гейт «открой в Telegram» здесь означал бы тупик после оплаты.
  const payReturn = window.location.pathname.startsWith('/pay/success')
    ? true
    : window.location.pathname.startsWith('/pay/fail')
      ? false
      : null;
  if (payReturn !== null && !tma.available) {
    return (
      <Suspense fallback={<Spinner />}>
        <PayReturnScreen ok={payReturn} botUrl={BOT_URL} />
      </Suspense>
    );
  }

  if (!tma.available) return <NotInTelegramScreen error={tma.error} />;

  return (
    <TmaProvider value={tma}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </trpc.Provider>
    </TmaProvider>
  );
}
